import { AbstractCameraCaptureAdapter } from './AbstractCameraCaptureAdapter';
import { ATBaseClient } from '@src/services/ATBaseClient';
import { BatchPhotoCaptureConfig, BatchPhotoUploadResult } from '@services/camera/CameraTypes';
import { subscribeBatchProgress, unsubscribeBatchProgress } from '@services/camera/CameraBatchEventChannel';
import { v4 as uuidv4 } from 'uuid';

/**
 * Cross-frame ceiling for one batch session. Must EXCEED the host-side
 * orchestrator ceiling in IhmAssociateToolsBase (15 min), so ATBase always
 * times out first and the tool receives a typed session-timeout rejection
 * rather than a raw cross-frame timeout. Nothing below this layer bounds a
 * batch session (the native capture timeout is disabled for batch and the
 * operator retry prompt is unbounded) — do NOT lower this toward the 60 s
 * cross-frame default; a batch session is associate-paced.
 */
const BATCH_CROSSFRAME_TIMEOUT_MILLIS = 15 * 60 * 1000 + 30 * 1000;

export class CrossFrameCameraCaptureAdapter extends AbstractCameraCaptureAdapter {
    private readonly atBaseClient = new ATBaseClient();

    public captureImage(config: any): Promise<any> {
        return this.atBaseClient.call('cameraCapture', 'captureImage', config);
    }

    public captureImageAndUpload(config: any): Promise<any> {
        return this.atBaseClient.call('cameraCapture', 'captureImageAndUpload', config);
    }

    /**
     * Captures several photos in one native session and uploads each one.
     *
     * One long-lived cross-frame request to the host frame's
     * `cameraBatchCapture` listener; the response arrives only when the
     * associate-paced session ends (capture -> review -> upload drain).
     * Per-photo progress cannot ride that single request, so
     * {@link BatchPhotoCaptureConfig.onPhotoUploaded} is stripped here (it
     * would not survive serialization anyway) and driven locally from the
     * host's pushes via the camera batch event channel, keyed by a
     * per-session id.
     *
     * Partial failure RESOLVES (failedIndices), an associate cancel RESOLVES
     * with `canceled: true` and whatever finalized first; only session-level
     * failures reject. On a cross-frame timeout the host is told to abandon
     * the session so remaining photos fail fast instead of each burning the
     * native 30 s destination timeout.
     */
    public captureImagesAndUpload(config: BatchPhotoCaptureConfig): Promise<BatchPhotoUploadResult> {
        const sessionId = uuidv4();
        const { onPhotoUploaded, ...parameters } = config ?? {};
        if (onPhotoUploaded) {
            subscribeBatchProgress(sessionId, onPhotoUploaded);
        }
        return this.atBaseClient
            .call(
                'cameraBatchCapture',
                'captureMultipleImagesAndUpload',
                { ...parameters, sessionId },
                { sensitive: false, crossFrameTimeoutMillis: BATCH_CROSSFRAME_TIMEOUT_MILLIS },
            )
            .catch((error: any) => {
                if (/timeout/i.test(`${error?.message ?? error}`)) {
                    // Fire-and-forget: the host declines the session's remaining
                    // destination requests instead of letting each one time out.
                    void this.atBaseClient.call('cameraBatchCapture', 'abandonSession', { sessionId }).catch(() => undefined);
                }
                throw error;
            })
            .finally(() => unsubscribeBatchProgress(sessionId)) as Promise<BatchPhotoUploadResult>;
    }

    /**
     * True: the cross-frame batch path is implemented end to end
     * (IhmAssociateToolsBase `cameraBatchCapture` listener, STARSHIP-64).
     * Still false on Velocity, which has no camera bridge at all.
     */
    public isBatchCaptureImplemented(): boolean {
        return true;
    }
}
